import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import EquipmentUsageCollapsibleTable from "../../components/table/EquipmentUsageCollapsibleTable";
import {
  EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
  EQUIPMENT_USAGE_TABLE_NAME,
  EITS_USAGE_TABLE_NAME,
  EITS_TABLE_FIELDS_CONFIG,
} from "../../components/table/EquipmentUsageCollapsibleTable/TableConfigs";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import UniversalToolBar from "../../components/bar/UniversalToolBar";
import { useAsync, useAsyncFn } from "../../hooks/useAsync";
import NavigationDrawer from "../../components/drawer/NavigationDrawer";
import { NAVIGATION_DRAWER_TYPE_METERS } from "../../components/drawer/NavigationDrawer/NavigationDrawer";
import useDialog from "../../hooks/useDialog";
import FilterDialog from "../../components/base/FilterDialog";
import UserFilterDialog from "../../components/dialog/UserFilterDialog";
import TableColumnVisibilityDialog from "../../components/dialog/TableColumnVisibilityDialog";
import UniversalFilterForm from "../../components/form/UniversalFilterForm";
import TableService from "../../services/tableService";
import useUserPreferencesService from "../../services/userPreferencesService";
import { useSnackbarAlert } from "../../context/snackbarAlert";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper/DefaultPageWrapper";
import { exportEquipmentUsageUrl } from "../../helpers/apiUrls";
import useEquipmentService from "../../services/equipmentService";
import DateField from "../../components/field/DateField/DateField";
import DateService from "../../services/dateService";
import { getObjectFromSearchParams } from "../../helpers/methods";
import IconButtonWithTooltip from "../../components/button/IconButtonWithTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChangeEquipmentUsageVerificationButton from "../../components/button/ChangeEquipmentUsageVerificationButton";

import useCheckPermission from "../../hooks/usePermission";
import { EDIT_COUNTER_USAGE_PERMISSION } from "../../helpers/constants";

const TABLE_CONFIGS = [
  {
    name: EQUIPMENT_USAGE_TABLE_NAME,
    config: EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
  },
  { name: EITS_USAGE_TABLE_NAME, config: EITS_TABLE_FIELDS_CONFIG },
];

export default function EquipmentUsagePage(props) {
  const { pageName } = props;

  const [hasPermissionToEditUsage] = useCheckPermission(
    EDIT_COUNTER_USAGE_PERMISSION
  );

  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const [usagePeriod, setUsagePeriod] = useState(() =>
    DateService.convertDateToFormatYYYYMMDD(DateService.getPreviousMonth())
  );

  const [equipmentUsageDataLocaly, setEquipmentUsageDataLocaly] = useState();
  const [countRecords, setCountRecords] = useState();

  const {
    page: equipmentUsageDataPage,
    pageSize: equipmentUsagePageSize,
    handleChangePageWithSearchParams: handleEquipmentUsageDataPage,
    handleChangePageSizeWithSearchParams:
      handleChangeEquipmentUsageDataPageSize,
    resetPageNumber,
    searchParams,
    setSearchParams,
  } = usePaginationWithSearchParams();

  const handleChangeRowsPerPage = (e) => {
    handleChangeEquipmentUsageDataPageSize(e, parseInt(e.target.value, 10));
  };

  const {
    getEquipmentUsageData,
    getEquipmentUsageFilteringData,
    recalculateEquipmentUsage,
  } = useEquipmentService();

  const usageSearchParams = useMemo(() => {
    let tempSearchParams = getObjectFromSearchParams(searchParams);
    tempSearchParams.equipment_usage_period = usagePeriod;
    return tempSearchParams;
  }, [searchParams, usagePeriod]);

  const equipmentUsageData = useAsync(() => {
    if (!usagePeriod) return Promise.resolve({});

    return getEquipmentUsageData(usageSearchParams);
  }, [usageSearchParams, usagePeriod]);

  const recalculateEquipmentUsageFn = useAsyncFn(recalculateEquipmentUsage);

  const {
    getUserPreferencesForPage,
    updateUserPreferencesForPage,
    getHiddenColumnFormValueFromBackend,
    getColumnOrdersFormValueFromBackend,
    convertUserPreferencesFromFrontendToBackend,
  } = useUserPreferencesService();

  const userPreferencesForPage = useAsync(
    () => getUserPreferencesForPage(pageName),
    [pageName]
  );

  const fullRefreshTable = () => {
    setEquipmentUsageDataLocaly(undefined);
    equipmentUsageData.refetch();
  };

  const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState();
  const [columnsOrdersForTables, setColumnsOrdersForTables] = useState();

  useEffect(() => {
    if (userPreferencesForPage.loading) {
      return;
    }
    setHiddenColumnsForTables(() =>
      getHiddenColumnFormValueFromBackend(
        [EQUIPMENT_USAGE_TABLE_NAME, EITS_USAGE_TABLE_NAME],
        userPreferencesForPage.value
      )
    );

    setColumnsOrdersForTables(() =>
      getColumnOrdersFormValueFromBackend(
        [EQUIPMENT_USAGE_TABLE_NAME, EITS_USAGE_TABLE_NAME],
        userPreferencesForPage.value
      )
    );
  }, [userPreferencesForPage.loading]);

  const updateUserPreferencesForPageFn = useAsyncFn(
    updateUserPreferencesForPage
  );

  const handleUpdateUserPreferences = (
    hiddenColumnsForTables,
    columnsOrdersForTables
  ) => {
    updateUserPreferencesForPageFn
      .execute(
        pageName,
        convertUserPreferencesFromFrontendToBackend(
          hiddenColumnsForTables,
          columnsOrdersForTables
        )
      )
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.table_columns_visibility_updated")
        );
        userPreferencesForPage.refetch();
        fullRefreshTable();
        onCloseTableColumnVisibilityDialog();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          t(
            "snackbar_alert.occurred_error_during_updating_table_columns_visibility"
          )
        );
      });
  };

  const handleRecalculateEquipmentUsage = (
    equipmentId,
    year,
    month,
    equipment_scheme
  ) => {
    recalculateEquipmentUsageFn
      .execute(equipmentId, year, month, year, month, equipment_scheme)
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.eits_usage_updated")
        );
        fullRefreshTable();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          t("snackbar_alert.occurred_error_during_saving_changes")
        );
      });
  };

  const equipmentUsageFilteringData = useAsync(getEquipmentUsageFilteringData);

  useEffect(() => {
    if (equipmentUsageData.loading) {
      return;
    }
    setEquipmentUsageDataLocaly(equipmentUsageData.value.results);
    setCountRecords(equipmentUsageData.value.count);
  }, [equipmentUsageData.loading]);

  const [openFilterDialog, onOpenFilterDialog, onCloseFilterDialog] =
    useDialog();

  const [
    openUserFiltersDialog,
    onOpenUserFiltersDialog,
    onCloseUserFiltersDialog,
  ] = useDialog();

  const [
    openTableColumnVisibilityDialog,
    onOpenTableColumnVisibilityDialog,
    onCloseTableColumnVisibilityDialog,
  ] = useDialog();

  const onCleanFlitersInFilterDialog = () => {
    setEquipmentUsageDataLocaly(undefined);
    setCountRecords(undefined);
  };

  const isLoading =
    userPreferencesForPage.loading ||
    hiddenColumnsForTables === undefined ||
    columnsOrdersForTables === undefined ||
    equipmentUsageFilteringData.loading ||
    equipmentUsageDataLocaly === undefined ||
    countRecords === undefined;

  const equipmentUsageExportHeaders = useMemo(() => {
    return TableService.getTableExportHeaders(
      EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
      hiddenColumnsForTables?.[EQUIPMENT_USAGE_TABLE_NAME],
      columnsOrdersForTables?.[EQUIPMENT_USAGE_TABLE_NAME]
    );
  }, [
    hiddenColumnsForTables?.[EQUIPMENT_USAGE_TABLE_NAME],
    columnsOrdersForTables?.[EQUIPMENT_USAGE_TABLE_NAME],
    EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG,
  ]);

  const handleClickCheck = useCallback((id) => {
    setEquipmentUsageDataLocaly((prevData) => {
      let selectedRow = { ...prevData.find((rowData) => rowData.id === id) };
      selectedRow.selected = !selectedRow.selected;
      return prevData.map((rowData) => {
        if (rowData.id === id) return selectedRow;
        return rowData;
      });
    });
  }, []);

  const [checkedAll, setCheckedAll] = useState(false);
  const handleClickCheckAll = useCallback(
    (value) => {
      let currValue = !checkedAll;
      setCheckedAll(currValue);

      setEquipmentUsageDataLocaly((prev) => {
        return prev.map((rowData) => ({
          ...rowData,
          selected: currValue,
        }));
      });
    },
    [checkedAll]
  );

  const getSeletedEquipmentUsageIds = () =>
    equipmentUsageDataLocaly
      ?.filter((equipmentUsage) => equipmentUsage.selected)
      .map((equipmentUsage) => equipmentUsage.id);

  return (
    <NavigationDrawer
      pageName={pageName}
      drawerType={NAVIGATION_DRAWER_TYPE_METERS}
    >
      <DefaultPageWrapper titleKey={"equipment_usage"}>
        <Grid
          container
          item
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item xs={12}>
            <UniversalToolBar
              style={{ marginBlock: "5px" }}
              onClickSearch={onOpenFilterDialog}
              onClickMyFilters={onOpenUserFiltersDialog}
              onClickAdjustTable={onOpenTableColumnVisibilityDialog}
              showOpenCloseFilterButton={false}
              showMassActionButton={false}
              showCreateButton={false}
              extraButtonList={[
                <Grid container>
                  <Grid item xs={2}>
                    <IconButtonWithTooltip
                      title={DateService.convertDateToFormatYYYYMM(
                        DateService.getMonthBehind(1, usagePeriod)
                      )}
                      onClick={() =>
                        setUsagePeriod(
                          DateService.convertDateToFormatYYYYMMDD(
                            DateService.getMonthBehind(1, usagePeriod)
                          )
                        )
                      }
                    >
                      <ArrowBackIcon />
                    </IconButtonWithTooltip>
                  </Grid>
                  <Grid item xs={8}>
                    <DateField
                      label={t("table.equipment_usage.month")}
                      value={usagePeriod}
                      onChange={(e) =>
                        setUsagePeriod(
                          DateService.convertDateToFormatYYYYMMDD(
                            e.target.value
                          )
                        )
                      }
                      showClearIcon={false}
                      view={"month"}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButtonWithTooltip
                      title={DateService.convertDateToFormatYYYYMM(
                        DateService.getMonthBehind(-1, usagePeriod)
                      )}
                      onClick={() =>
                        setUsagePeriod(
                          DateService.convertDateToFormatYYYYMMDD(
                            DateService.getMonthBehind(-1, usagePeriod)
                          )
                        )
                      }
                    >
                      <ArrowForwardIcon />
                    </IconButtonWithTooltip>
                  </Grid>
                </Grid>,
                hasPermissionToEditUsage ? (
                  <ChangeEquipmentUsageVerificationButton
                    selectedEquipmentUsageIds={getSeletedEquipmentUsageIds()}
                    onSubmit={equipmentUsageData.refetch}
                  />
                ) : null,
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <LoaderWrapper showLoader={isLoading}>
              <EquipmentUsageCollapsibleTable
                data={equipmentUsageDataLocaly}
                showCheckbox={true}
                checkedAll={checkedAll}
                onClickCheckAll={handleClickCheckAll}
                onClickCheck={handleClickCheck}
                showDetailsIcon={false}
                showCleanFilterIcon={false}
                countRecords={countRecords}
                page={equipmentUsageDataPage}
                onPageChange={handleEquipmentUsageDataPage}
                rowsPerPage={equipmentUsagePageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                resetPageNumber={resetPageNumber}
                filteringData={equipmentUsageFilteringData}
                hiddenColumns={hiddenColumnsForTables}
                columnsOrders={columnsOrdersForTables}
                refreshEquipmentUsageTable={equipmentUsageData.refetch}
                style={{ maxHeight: "80vh" }}
                showExportToFileButton={true}
                exportToFileUrl={exportEquipmentUsageUrl}
                exportToFileSearchParams={usageSearchParams}
                exportToFileHeaders={equipmentUsageExportHeaders}
                exportToFileFileName={`${t("headers.equipment_usage")}.xlsx`}
                isLoading={isLoading || equipmentUsageData.loading}
                usagePeriod={usagePeriod}
                onClickRecalculate={handleRecalculateEquipmentUsage}
                readOnly={!hasPermissionToEditUsage}
              />
            </LoaderWrapper>
          </Grid>
        </Grid>
      </DefaultPageWrapper>
      <FilterDialog
        open={openFilterDialog}
        onCleanFilters={onCleanFlitersInFilterDialog}
        onClose={onCloseFilterDialog}
        resetPageNumber={resetPageNumber}
        filterForm={
          <UniversalFilterForm
            filteringData={equipmentUsageFilteringData}
            filtersConfig={EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG}
            includeOpenCloseFilter={true}
            includeHasChangesDotFilter={true}
          />
        }
      />
      {openUserFiltersDialog && (
        <UserFilterDialog
          open={openUserFiltersDialog}
          onClose={onCloseUserFiltersDialog}
          pageName={pageName}
          filterForm={
            <UniversalFilterForm
              filteringData={equipmentUsageFilteringData}
              filtersConfig={EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG}
              includeOpenCloseFilter={false}
              includeHasChangesDotFilter={false}
            />
          }
        />
      )}
      {openTableColumnVisibilityDialog && (
        <TableColumnVisibilityDialog
          open={openTableColumnVisibilityDialog}
          onClose={onCloseTableColumnVisibilityDialog}
          onSubmit={handleUpdateUserPreferences}
          tablesConfigs={TableService.getTableConfigsForTableColumnVisibilityDialog(
            TABLE_CONFIGS,
            columnsOrdersForTables
          )}
          tablesHiddenColumns={hiddenColumnsForTables}
          isLoading={userPreferencesForPage.loading}
        />
      )}
    </NavigationDrawer>
  );
}
